import React from 'react';
import {
    Button,
    Input,
    Dropdown,
    Icon
} from 'gd-react';
import { StateOptions } from 'gd-react';
import './SingleCalculatedTypeEditor.scss';

class SingleCalculatedTypeEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            typeKey: props.typeKey || '',
            dataType: props.dataType || '',
            calculation: props.calculation || {
                operations: [],
                finalUnit: '',
                description: ''
            }
        };
        
        this.updateCalculation = this.updateCalculation.bind(this);
        this.addParameter = this.addParameter.bind(this);
        this.removeParameter = this.removeParameter.bind(this);
        this.updateParameter = this.updateParameter.bind(this);
        this.getAvailableDataTypes = this.getAvailableDataTypes.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.typeKey !== this.props.typeKey || 
            prevProps.dataType !== this.props.dataType || 
            prevProps.calculation !== this.props.calculation) {
            this.setState({
                typeKey: this.props.typeKey || '',
                dataType: this.props.dataType || '',
                calculation: this.props.calculation || {
                    operations: [],
                    finalUnit: '',
                    description: ''
                }
            });
        }
    }

    getAvailableDataTypes() {
        const dataTypes = [];
        StateOptions.forEach(stateOption => {
            stateOption.dataTypes.forEach(dataType => {
                if (!dataTypes.find(dt => dt.value === dataType.type)) {
                    dataTypes.push({
                        value: dataType.type,
                        title: `${dataType.label} (${dataType.unit})`
                    });
                }
            });
        });
        return dataTypes.sort((a, b) => a.title.localeCompare(b.title));
    }

    updateCalculation(field, value) {
        const newCalculation = {
            ...this.state.calculation,
            [field]: value
        };
        
        this.setState({ calculation: newCalculation }, () => {
            if (this.props.onChange) {
                this.props.onChange({
                    typeKey: this.state.typeKey,
                    dataType: this.state.dataType,
                    calculation: this.state.calculation
                });
            }
        });
    }

    addParameter() {
        const newCalculation = {
            ...this.state.calculation,
            operations: [
                {
                    order: 1,
                    operation: 'multiply',
                    parameters: [
                        {
                            name: `param_${Date.now()}`,
                            value: '',
                            description: ''
                        }
                    ],
                    description: ''
                }
            ]
        };
        
        this.setState({ calculation: newCalculation }, () => {
            if (this.props.onChange) {
                this.props.onChange({
                    typeKey: this.state.typeKey,
                    dataType: this.state.dataType,
                    calculation: this.state.calculation
                });
            }
        });
    }

    removeParameter() {
        const newCalculation = {
            ...this.state.calculation,
            operations: []
        };
        
        this.setState({ calculation: newCalculation }, () => {
            if (this.props.onChange) {
                this.props.onChange({
                    typeKey: this.state.typeKey,
                    dataType: this.state.dataType,
                    calculation: this.state.calculation
                });
            }
        });
    }

    updateParameter(field, value) {
        if (!this.state.calculation.operations || this.state.calculation.operations.length === 0) {
            return;
        }

        const newCalculation = {
            ...this.state.calculation,
            operations: [
                {
                    ...this.state.calculation.operations[0],
                    parameters: [
                        {
                            ...this.state.calculation.operations[0].parameters[0],
                            [field]: value
                        }
                    ]
                }
            ]
        };
        
        this.setState({ calculation: newCalculation }, () => {
            if (this.props.onChange) {
                this.props.onChange({
                    typeKey: this.state.typeKey,
                    dataType: this.state.dataType,
                    calculation: this.state.calculation
                });
            }
        });
    }

    render() {
        const { typeKey, dataType, calculation } = this.state;
        const dataTypes = this.getAvailableDataTypes();
        const hasParameters = calculation.operations && calculation.operations.length > 0 && 
                             calculation.operations[0].parameters && calculation.operations[0].parameters.length > 0;
        const parameter = hasParameters ? calculation.operations[0].parameters[0] : null;

        return (
            <div className="single-calculated-type-editor">
                <div className="editor-header">
                    <h3>Edit Calculated Data Type</h3>
                    <p>Configure the formula for: <strong>{typeKey}</strong></p>
                </div>

                <div className="editor-content">
                    <div className="form-section">
                        <h4>Basic Settings</h4>
                        <div className="form-fields">
                            <Dropdown
                                label="Calculated Data Type"
                                value={dataType}
                                fixeditems={dataTypes}
                                onChange={(e) => this.setState({ dataType: e.target.value }, () => {
                                    if (this.props.onChange) {
                                        this.props.onChange({
                                            typeKey: this.state.typeKey,
                                            dataType: this.state.dataType,
                                            calculation: this.state.calculation
                                        });
                                    }
                                })}
                                placeholder="Select calculated data type"
                            />
                            <Input
                                label="Formula Description"
                                value={calculation.description}
                                onChange={(e) => this.updateCalculation('description', e.target.value)}
                                placeholder="What does this formula calculate?"
                            />
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="section-header">
                            <h4>Formula Parameters</h4>
                            {hasParameters ? (
                                <Button
                                    label="Remove Parameter"
                                    icon="FaTrash"
                                    color="gd-red"
                                    onClick={this.removeParameter}
                                    additionalclasses="sm"
                                />
                            ) : (
                                <Button
                                    label="Add Parameter"
                                    icon="FaPlus"
                                    color="gd-green"
                                    onClick={this.addParameter}
                                    additionalclasses="sm"
                                />
                            )}
                        </div>

                        {hasParameters ? (
                            <div className="parameter-editor">
                                <div className="parameter-fields">
                                    <Input
                                        label="Description"
                                        value={parameter.description}
                                        onChange={(e) => this.updateParameter('description', e.target.value)}
                                        placeholder="What is this parameter for?"
                                    />
                                    <Input
                                        label="Value"
                                        value={parameter.value}
                                        onChange={(e) => this.updateParameter('value', e.target.value)}
                                        placeholder="Enter the parameter value"
                                    />
                                </div>
                                <div className="parameter-info">
                                    <Icon icon="FaInfoCircle" size={14} />
                                    <span>Parameter name is automatically generated from the description</span>
                                </div>
                            </div>
                        ) : (
                            <div className="no-parameters">
                                <p>No parameters configured. Add a parameter to define your formula.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SingleCalculatedTypeEditor;
